import { Injectable } from '@angular/core';
import { EventBusService, EventType } from './event-bus.service';
import { Region } from '../types/region.type';
import { ApiService } from './api/api.service';

@Injectable({ providedIn: 'root' })
export class RegionCacheService {
  /**
   * ISO region codes that are prioritized for display in the UI.
   * These regions will be shown first in the list of regions.
   */
  readonly prioIsoRegionCodes = ['US', 'CA', 'GB', 'AT', 'CZ', 'DE', 'PL', 'SK'];

  get regions(): Region[] {
    return this._regions;
  }

  get priorityRegions(): Region[] {
    return this._priorityRegions;
  }

  get nonPriorityRegions(): Region[] {
    return this._nonPriorityRegions;
  }

  private _regions: Region[];
  private _priorityRegions: Region[] = [];
  private _nonPriorityRegions: Region[] = [];

  private initializationPromise: Promise<Region[]>;

  constructor(eventBusService: EventBusService, private apiService: ApiService) {
    eventBusService.on(EventType.OrganizationChanged).subscribe(() => this.clear());
  }

  public async initialize(): Promise<void> {
    if (this._regions != null) {
      return;
    }

    if (this.initializationPromise != null) {
      await this.initializationPromise;
      return;
    }

    this.initializationPromise = this.apiService.getOrganizationSupportedRegions();
    this._priorityRegions = [];
    this._nonPriorityRegions = [];
    try {
      const regions = await this.initializationPromise;
      regions.sort((a, b) => a.englishName.localeCompare(b.englishName));
      regions.forEach(region => {
        if (this.prioIsoRegionCodes.includes(region.isoRegionName)) {
          region.priority = this.prioIsoRegionCodes.indexOf(region.isoRegionName);
          this._priorityRegions.push(region);
        } else {
          this._nonPriorityRegions.push(region);
        }
      });
      this._priorityRegions.sort((a, b) => a.priority - b.priority);
      this._regions = [...this._priorityRegions, ...this._nonPriorityRegions];
    } catch (error) {
      this.initializationPromise = null;
      throw error;
    }
  }

  getRegionName(isoRegionName: string): string {
    return this.getRegion(isoRegionName)?.englishName;
  }

  getRegionNamesString(isoRegionNames: string[]): string {
    return this.getRegionNames(isoRegionNames).join(', ');
  }

  getRegionsByIsoRegionNames(isoRegionNames: string[], all = false): Region[] {
    if (!isoRegionNames?.length && all) {
      return this._regions;
    }
    return this._regions?.filter(r => isoRegionNames.includes(r.isoRegionName));
  }

  getDefaultRegion(): string | null {
    if (this._regions == null || this._regions.length === 0) {
      return null;
    }
    // Return the first region in the list as the default region
    return this._regions[0].isoRegionName;
  }

  clear(): void {
    this._regions = null;
    this.initializationPromise = null;
  }

  private getRegion(isoRegionName: string): Region {
    return this._regions?.find(r => r.isoRegionName === isoRegionName);
  }

  private getRegionNames(isoRegionNames: string[]): string[] {
    return isoRegionNames.map(isoRegionName => this.getRegionName(isoRegionName));
  }
}
